import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import DataConnector from 'connect/DataConnector';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/bs/Tooltip';
import {GRAY} from 'ui-library/Colors';
import TimezonePickerDropdown from 'ui-library/dropdowns/TimezonePickerDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import waitForEntrySaved from '../utils/waitForEntrySaved';
import * as AutomationFileDAO from './AutomationFileDAO';

interface AutomationOutputFileTimezonePickerProps {
  runId: string | null;
  automationOutputFile: AutomationOutputFileGraphQL;
  locked: boolean;
  updateAutomationOutputFile: (automationOutputFile: AutomationOutputFileGraphQL) => void;
}

interface ConnectedProps {
  outputFileStatus: AutomationFileStatusGraphQL | null;
}

class AutomationOutputFileTimezonePickerConnector extends DataConnector<
  AutomationOutputFileTimezonePickerProps,
  ConnectedProps
> {
  enhanceProps(): ConnectedProps {
    return {
      outputFileStatus: this.props.automationOutputFile.id
        ? this.reduxState().ui.automationFileStatus[this.props.automationOutputFile.id]
        : null,
    };
  }
}

const AutomationOutputFileTimezonePicker = (
  props: AutomationOutputFileTimezonePickerProps & ConnectedProps
): JSX.Element | null => {
  const [isWaitingForSave, setWaitingForSave] = useState(false);
  const automationOutputFile = props.automationOutputFile;
  const isWritable =
    !props.locked &&
    props.outputFileStatus !== AutomationFileStatusGraphQL.running &&
    automationOutputFile.status === AutomationFileStatusGraphQL.pending;
  return (
    <Row className="u-mini-spacing-top" bufferRowReset={true}>
      <Col md={3}>
        <span className="fieldLabel-sublabel">Default Timezone</span>
        <Tooltip title="Timezone to use for date and datetime fields if not specified in spreadsheet">
          <BenchlingIcon className={css(timezonePickerStyles.infoIcon)} icon="more-info" />
        </Tooltip>
      </Col>
      <Col md={9}>
        <EditorPropertiesContext.Consumer>
          {({fileId}) => (
            <TimezonePickerDropdown
              disabled={!isWritable || isWaitingForSave}
              value={automationOutputFile.timezone}
              onChange={async (timezone: string): Promise<void> => {
                setWaitingForSave(true);
                try {
                  const saved = fileId != null ? await waitForEntrySaved(fileId) : true;
                  if (saved) {
                    const response = await AutomationFileDAO.updateAutomationOutputFileParams(
                      automationOutputFile.id,
                      {timezone}
                    );
                    props.updateAutomationOutputFile(response.automationOutputFile);
                  }
                } finally {
                  setWaitingForSave(false);
                }
              }}
              menuProps={{
                height: Menu.HEIGHTS.MEDIUM,
                width: Menu.WIDTHS.FULL,
              }}
            />
          )}
        </EditorPropertiesContext.Consumer>
      </Col>
    </Row>
  );
};

export const timezonePickerStyles = StyleSheet.create({
  infoIcon: {
    marginLeft: '4px',
    color: GRAY,
  },
});

export default AutomationOutputFileTimezonePickerConnector.connect(AutomationOutputFileTimezonePicker);
